import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { IconDatabaseOff, IconRefresh } from '@tabler/icons-react';
import Editor from '@monaco-editor/react';
import { EditorModeToggle } from '../components/admin/EditorModeToggle';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import { CARD_SX } from '../styles/designSystem';
import type { TicketTypeTemplateCatalog } from '../types/ticketTypeTemplate';
import {
  getTicketTypeTemplates,
  putTicketTypeTemplates,
  clearTicketTypeTemplatesCache,
} from '../services/ticketTypeTemplatesService';
import { TicketTypeTemplateGuiEditor } from '../components/admin/templates/TicketTypeTemplateGuiEditor';
import { useJsonGuiToggle, type EditorMode } from '../hooks/useJsonGuiToggle';

interface ValidationError {
  message: string;
}

export function validateCatalog(data: unknown): ValidationError[] {
  const errors: ValidationError[] = [];
  if (typeof data !== 'object' || data === null) {
    return [{ message: 'Root must be a JSON object' }];
  }
  const obj = data as Record<string, unknown>;
  const templates = obj.templates;
  if (!Array.isArray(templates)) {
    errors.push({ message: 'templates must be an array' });
    return errors;
  }
  const seenIds = new Set<string>();
  for (let i = 0; i < templates.length; i++) {
    const t = templates[i];
    if (typeof t !== 'object' || t === null) {
      errors.push({ message: `templates[${i}] must be an object` });
      continue;
    }
    const tobj = t as Record<string, unknown>;
    const id = tobj.ticketTypeId;
    if (!id || typeof id !== 'string') {
      errors.push({ message: `templates[${i}] missing ticketTypeId` });
    } else if (seenIds.has(id)) {
      errors.push({ message: `Duplicate ticketTypeId: ${id}` });
    } else {
      seenIds.add(id);
    }
    if (!tobj.displayName) {
      errors.push({ message: `templates[${i}] missing displayName` });
    }
    if (!tobj.group) {
      errors.push({ message: `templates[${i}] missing group` });
    }

    // Validate additionalFields: dup fieldKey, license-picker config, dropdown options.
    const fields = tobj.additionalFields;
    if (fields !== undefined && !Array.isArray(fields)) {
      errors.push({ message: `templates[${i}].additionalFields must be an array` });
    } else if (Array.isArray(fields)) {
      const seenKeys = new Set<string>();
      fields.forEach((f, fi) => {
        if (typeof f !== 'object' || f === null) return;
        const fobj = f as Record<string, unknown>;
        const key = fobj.fieldKey;
        if (!key || typeof key !== 'string') {
          errors.push({ message: `templates[${i}].additionalFields[${fi}] missing fieldKey` });
        } else if (seenKeys.has(key)) {
          errors.push({
            message: `templates[${i}].additionalFields: duplicate fieldKey "${key}"`,
          });
        } else {
          seenKeys.add(key);
        }
        if (fobj.type === 'license-picker' && (!fobj.licensePicker || typeof fobj.licensePicker !== 'object')) {
          errors.push({
            message: `templates[${i}].additionalFields[${fi}]: license-picker requires licensePicker config`,
          });
        }
        if (
          fobj.type === 'dropdown' &&
          (!Array.isArray(fobj.options) || (fobj.options as unknown[]).length === 0)
        ) {
          errors.push({
            message: `templates[${i}].additionalFields[${fi}]: dropdown requires at least one option`,
          });
        }
      });
    }

    // Validate followUpTodos the way sm4rt-api will: a date field of THIS
    // template, a whole-day offset, a title, and a target in the catalog.
    const followUps = tobj.followUpTodos;
    if (followUps !== undefined && followUps !== null) {
      if (!Array.isArray(followUps)) {
        errors.push({ message: `templates[${i}].followUpTodos must be an array` });
      } else {
        const dateKeys = new Set(
          (Array.isArray(fields) ? fields : [])
            .filter((f) => f && typeof f === 'object' && (f as Record<string, unknown>).type === 'date')
            .map((f) => (f as Record<string, unknown>).fieldKey),
        );
        const catalogIds = new Set(
          templates.map((tt) => (tt && typeof tt === 'object' ? (tt as Record<string, unknown>).ticketTypeId : undefined)),
        );
        followUps.forEach((fu, fi) => {
          const where = `templates[${i}].followUpTodos[${fi}]`;
          const f = (fu ?? {}) as Record<string, unknown>;
          if (typeof f.ticketTypeId !== 'string' || !catalogIds.has(f.ticketTypeId)) {
            errors.push({ message: `${where}: ticketTypeId must name a template in this catalog` });
          }
          if (!dateKeys.has(f.dateField)) {
            errors.push({ message: `${where}: dateField must be one of this template's date fields` });
          }
          if (!Number.isInteger(f.offsetDays)) {
            errors.push({ message: `${where}: offsetDays must be a whole number` });
          }
          if (typeof f.titleTemplate !== 'string' || !f.titleTemplate.trim()) {
            errors.push({ message: `${where}: titleTemplate is required` });
          }
        });
      }
    }

    // Validate templateLineItems: dup templateKey.
    const items = tobj.templateLineItems;
    if (items !== undefined && !Array.isArray(items)) {
      errors.push({ message: `templates[${i}].templateLineItems must be an array` });
    } else if (Array.isArray(items)) {
      const seenKeys = new Set<string>();
      items.forEach((li, li_i) => {
        if (typeof li !== 'object' || li === null) return;
        const liobj = li as Record<string, unknown>;
        const key = liobj.templateKey;
        if (!key || typeof key !== 'string') {
          errors.push({
            message: `templates[${i}].templateLineItems[${li_i}] missing templateKey`,
          });
        } else if (seenKeys.has(key)) {
          errors.push({
            message: `templates[${i}].templateLineItems: duplicate templateKey "${key}"`,
          });
        } else {
          seenKeys.add(key);
        }
      });
    }
  }
  return errors;
}

const EMPTY_CATALOG: TicketTypeTemplateCatalog = { templates: [] };

export const TicketTypeTemplatesAdminPage: React.FC = () => {
  const [catalog, setCatalog] = useState<TicketTypeTemplateCatalog>(EMPTY_CATALOG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [clearing, setClearing] = useState(false);

  const { mode, jsonText, setJsonText, switchMode, syncJsonFromValue } =
    useJsonGuiToggle<TicketTypeTemplateCatalog>({
      value: catalog,
      onChange: setCatalog,
      initialMode: 'gui',
      validate: (parsed) => validateCatalog(parsed).map((e) => e.message),
    });

  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTicketTypeTemplates(signal);
      setCatalog(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load ticket type templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  useEffect(() => {
    if (mode === 'gui') syncJsonFromValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  const handleSelectMode = (newMode: EditorMode) => {
    const result = switchMode(newMode);
    if (!result.ok) setError(result.error ?? 'Cannot switch modes');
  };

  const handleCatalogChange = (updated: TicketTypeTemplateCatalog) => {
    setCatalog(updated);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setValidationErrors([]);

    let dataToSave: TicketTypeTemplateCatalog;

    if (mode === 'json') {
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonText);
      } catch {
        setError('Invalid JSON');
        return;
      }
      const errors = validateCatalog(parsed);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      dataToSave = parsed as TicketTypeTemplateCatalog;
    } else {
      const errors = validateCatalog(catalog);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      dataToSave = catalog;
    }

    setSaving(true);
    try {
      const saved = await putTicketTypeTemplates(dataToSave);
      setCatalog(saved);
      clearTicketTypeTemplatesCache().catch(() => {});
      setSuccess('Ticket type templates saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClearCache = async () => {
    setClearing(true);
    try {
      const result = await clearTicketTypeTemplatesCache();
      setSuccess(result.cleared ? 'Cache cleared' : 'Cache was already empty');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cache');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const feedback = (error || success || validationErrors.length > 0) ? (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Validation errors:
          </Typography>
          {validationErrors.map((e, i) => (
            <Typography key={i} variant="body2">
              - {e.message}
            </Typography>
          ))}
        </Alert>
      )}
    </>
  ) : null;

  return (
    <>
      {feedback && <Box sx={{ px: '16px', pt: '10px' }}>{feedback}</Box>}

      {mode === 'gui' ? (
        <TicketTypeTemplateGuiEditor
          mode={mode}
          onModeChange={handleSelectMode}
          catalog={catalog}
          onCatalogChange={handleCatalogChange}
          onSave={handleSave}
          onClearCache={handleClearCache}
          onReload={() => loadData()}
          saving={saving}
          clearing={clearing}
        />
      ) : (
        <Box>
          <SecondaryToolbar floating>
            <EditorModeToggle mode={mode} onChange={handleSelectMode} />
            <ToolbarSpacer />
            <ToolbarButton variant="primary" onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </ToolbarButton>
            <ToolbarButton icon={IconDatabaseOff} onClick={handleClearCache}>
              {clearing ? 'Clearing…' : 'Clear Cache'}
            </ToolbarButton>
            <ToolbarButton icon={IconRefresh} onClick={() => loadData()}>
              Reload
            </ToolbarButton>
          </SecondaryToolbar>
          <Box sx={{ p: '12px 16px 24px' }}>
            <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
              <Editor
                height="70vh"
                defaultLanguage="json"
                value={jsonText}
                onChange={(value) => setJsonText(value ?? '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  formatOnPaste: true,
                  scrollBeyondLastLine: false,
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
